import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';

import { routeTree } from './routeTree.gen';
import { queryClient } from '@/lib/queryClient';
import { initPostHog } from '@/lib/posthog';
import { capturePageView } from '@/lib/analytics';
import { configureApiClient } from '@/lib/api';
import { installChunkReloadHandler } from '@/lib/chunkReload';
import './index.css';

configureApiClient();

// Recover from a blank page when a lazily imported chunk fails to load —
// either because a deploy rotated the content-hashed filenames under a tab
// that is still open, or because Pages served its text/html SPA fallback for
// a chunk that had not yet reached the edge. Must be installed before the
// router mounts and starts importing route chunks.
installChunkReloadHandler();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

// Subscribe to resolved navigations to capture page_view events.
// We use the route's fullPath pattern (e.g. /apartment/$id) rather than
// location.pathname so bearer tokens and IDs are never sent to PostHog.
// 'onResolved' fires after the new location is fully committed, once per
// navigation — including the initial load.
router.subscribe('onResolved', ({ toLocation }) => {
  // toLocation.pathname matches the route pattern string in TanStack Router
  // (it is the pathname of the resolved route, not the raw URL). For file-
  // based routes the last match's routeId equals the pattern; use that when
  // available to ensure token-like dynamic segments are represented as $param.
  const matches = router.state.matches;
  const lastMatch = matches.at(-1);
  const pattern = lastMatch?.routeId ?? toLocation.pathname;
  capturePageView(pattern);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Typed router history state. The booking widget passes the hold context to the
// checkout page via navigate state, which paints instantly with no round trip.
// GET /holds/:id (Task 12, OH-62) is the authoritative source once it lands
// and is what the checkout page falls back to when this state is empty (a
// hard reload / direct navigation arrives without it). All fields here stay
// optional for that reason.
declare module '@tanstack/history' {
  interface HistoryState {
    holdTotalMinor?: number;
    holdCurrency?: string;
    holdExpiresAt?: string;
    apartmentTitle?: string;
    // OH-82: the id, not just the title. When a hold dies the checkout page
    // offers "pick these dates again", and that link needs an apartment to
    // point at — the hold fetch answers 404 for a dead hold and carries
    // nothing, so without this the guest lands back on the full list.
    apartmentId?: string;
    checkinDate?: string;
    checkoutDate?: string;
  }
}

initPostHog();

const container = document.getElementById('root');
if (!container) {
  throw new Error('root element missing from index.html');
}

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
